import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  TextField,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider,
  CircularProgress,
  InputAdornment,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: theme.spacing(2),
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
    width: "100%",
    "& .MuiTab-wrapper": {
      color: theme.palette.fontecor,
    },
    "& .MuiTabs-flexContainer": {
      justifyContent: "center",
    },
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  sectionDivider: {
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
  },
  integrationCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  integrationTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

// Opções de configuração
const OPTIONS = {
  userRating: [
    { value: "disabled", label: "Desabilitadas" },
    { value: "enabled", label: "Habilitadas" },
  ],
  scheduleType: [
    { value: "disabled", label: "Desabilitado" },
    { value: "queue", label: "Por fila" },
    { value: "company", label: "Por empresa" },
  ],
  call: [
    { value: "disabled", label: "Não aceitar" },
    { value: "enabled", label: "Aceitar" },
  ],
  CheckMsgIsGroup: [
    { value: "disabled", label: "Desativado" },
    { value: "enabled", label: "Ativado" },
  ],
  sendGreetingAccepted: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  sendMsgTransfTicket: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
};

// Componente reutilizável de select com descrição
const SelectOption = ({ label, helperText, value, onChange, loading, options, className }) => (
  <FormControl className={className} fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
    <FormHelperText>
      {loading ? "Atualizando..." : helperText}
    </FormHelperText>
  </FormControl>
);

// TextField com debounce para integrações
const DebouncedTextField = ({ value: externalValue, onSave, loading, label, ...props }) => {
  const [value, setValue] = useState(externalValue || "");
  const timerRef = useRef(null);

  useEffect(() => {
    setValue(externalValue || "");
  }, [externalValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onSave(newValue);
    }, 800);
  };

  return (
    <TextField
      {...props}
      label={label}
      value={value}
      onChange={handleChange}
      variant="outlined"
      margin="dense"
      fullWidth
      InputProps={{
        endAdornment: loading ? (
          <InputAdornment position="end">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`options-tabpanel-${index}`}
      aria-labelledby={`options-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

export default function Options({ settings, scheduleTypeChanged }) {
  const classes = useStyles();
  const { update } = useSettings();

  const [tabIndex, setTabIndex] = useState(0);
  const [settingsState, setSettingsState] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    if (Array.isArray(settings)) {
      const initial = {};
      settings.forEach((s) => {
        if (s.key) initial[s.key] = s.value;
      });
      setSettingsState(initial);

      if (typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(initial.scheduleType === "company");
      }
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSetting = async (key, value, loaderKey = key) => {
    setLoading((prev) => ({ ...prev, [loaderKey]: true }));
    try {
      await update({ key, value });
      setSettingsState((prev) => ({ ...prev, [key]: value }));
      toast.success("Configuração atualizada.");

      if (key === "scheduleType" && typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(value === "company");
      }
    } catch (err) {
      toast.error("Erro ao atualizar configuração.");
    } finally {
      setLoading((prev) => ({ ...prev, [loaderKey]: false }));
    }
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tab}
      >
        <Tab label="Geral" />
        <Tab label="Integrações" />
      </Tabs>

      {/* Aba Geral */}
      <TabPanel value={tabIndex} index={0}>

        {/* Grupo: Atendimento */}
        <Typography className={classes.sectionTitle}>Atendimento</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Avaliações"
              helperText="Permite que clientes avaliem o atendimento ao encerrar."
              value={settingsState.userRating || "disabled"}
              onChange={(value) => updateSetting("userRating", value)}
              loading={loading.userRating}
              options={OPTIONS.userRating}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Saudação ao aceitar ticket"
              helperText="Envia mensagem automática quando agente aceita o atendimento."
              value={settingsState.sendGreetingAccepted || "disabled"}
              onChange={(value) => updateSetting("sendGreetingAccepted", value)}
              loading={loading.sendGreetingAccepted}
              options={OPTIONS.sendGreetingAccepted}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Mensagem ao transferir ticket"
              helperText="Notifica o cliente quando o atendimento é transferido entre agentes."
              value={settingsState.sendMsgTransfTicket || "disabled"}
              onChange={(value) => updateSetting("sendMsgTransfTicket", value)}
              loading={loading.sendMsgTransfTicket}
              options={OPTIONS.sendMsgTransfTicket}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Aceitar chamadas de voz"
              helperText="Define se a plataforma aceita chamadas de voz via WhatsApp."
              value={settingsState.call || "disabled"}
              onChange={(value) => updateSetting("call", value)}
              loading={loading.call}
              options={OPTIONS.call}
            />
          </Grid>
        </Grid>

        <Divider className={classes.sectionDivider} />

        {/* Grupo: Sistema */}
        <Typography className={classes.sectionTitle}>Sistema</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Controle de expediente"
              helperText="Define se o horário de atendimento é controlado por fila ou pela empresa."
              value={settingsState.scheduleType || "disabled"}
              onChange={(value) => updateSetting("scheduleType", value)}
              loading={loading.scheduleType}
              options={OPTIONS.scheduleType}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Ignorar mensagens de grupos"
              helperText="Quando ativado, mensagens enviadas em grupos do WhatsApp são ignoradas."
              value={settingsState.CheckMsgIsGroup || "disabled"}
              onChange={(value) => updateSetting("CheckMsgIsGroup", value)}
              loading={loading.CheckMsgIsGroup}
              options={OPTIONS.CheckMsgIsGroup}
            />
          </Grid>
        </Grid>
      </TabPanel>

      {/* Aba Integrações */}
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Configure as integrações com sistemas externos. Os campos são salvos automaticamente após parar de digitar.
        </Typography>

        {/* IXC */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            IXC
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DebouncedTextField
                label="IP do IXC"
                value={settingsState.ipixc}
                onSave={(value) => updateSetting("ipixc", value)}
                loading={loading.ipixc}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DebouncedTextField
                label="Token do IXC"
                value={settingsState.tokenixc}
                onSave={(value) => updateSetting("tokenixc", value)}
                loading={loading.tokenixc}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* MK-AUTH */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            MK-AUTH
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="IP do MK-Auth"
                value={settingsState.ipmkauth}
                onSave={(value) => updateSetting("ipmkauth", value)}
                loading={loading.ipmkauth}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="Client ID"
                value={settingsState.clientidmkauth}
                onSave={(value) => updateSetting("clientidmkauth", value)}
                loading={loading.clientidmkauth}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="Client Secret"
                value={settingsState.clientsecretmkauth}
                onSave={(value) => updateSetting("clientsecretmkauth", value)}
                loading={loading.clientsecretmkauth}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ASAAS */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            ASAAS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DebouncedTextField
                label="Token ASAAS"
                value={settingsState.asaas}
                onSave={(value) => updateSetting("asaas", value)}
                loading={loading.asaas}
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>
    </Paper>
  );
}
